/**
 * Scroll reveal engine.
 *
 * Marks existing elements with `data-v-reveal` so src/styles/animations.css can
 * play a one-off entrance animation when they enter the viewport. Nothing is
 * wrapped or restructured, and the attribute is cleared once the animation has
 * played so component styles (including :hover transforms) stay untouched.
 */

const REVEAL_ATTR = "data-v-reveal";
const ANIM_ATTR = "data-v-anim";

/* Longest possible delay + duration, used as a backstop for `animationend`. */
const SETTLE_TIMEOUT = 1400;

/* Section headings, text blocks, grid items and lists: fade up. */
const FADE_UP = [
  ".future-content",
  ".whyus-top",
  ".welaunch-left",
  ".industries-content",
  ".capabilitiestop",
  ".liftoff-left",
  ".liftoff-right",
  ".portfolio-content",
  ".extra-card",
  ".servicescap-top",
  ".solve__label",
  ".solve__container > h2",
  ".solve__subtitle",
  ".included__label",
  ".included__container > h2",
  ".included__subtitle",
  ".process__label",
  ".process__container > h2",
  ".process__subtitle",
  ".hosting__label",
  ".hosting__container > h2",
  ".hosting__subtitle",
  ".projects__title",
  ".morework__title",
  ".trusted__subtitle",
  ".trusted__title",
  ".trusted__desc",
  ".trusted__logos",
  ".bp-header",
  ".gs-subtitle",
  ".gs-title",
  ".ceo-content",
  ".jobs__header",
  ".culture__left",
  ".ch-header",
  ".enables-subtitle",
  ".enables-title",
  ".enables-description",
  ".wwb-left",
  ".relevant-header",
  ".latestblog__top",
  ".contact__header",
  ".contact-form__label",
  ".contact-form__subtitle",
  ".quote-form__title",
  ".quote-form__subtitle",
  ".order-hosting__title",
  ".order-hosting__subtitle",
  ".footer-left",
  ".future-stats .stat",
  ".why-card",
  ".industry-tag",
  ".cap-card",
  ".service-card",
  ".solve__card",
  ".included__item",
  ".process__card",
  ".project",
  ".mw-card",
  ".bp-card",
  ".gs-stat",
  ".job-card",
  ".article-card",
  ".latestblog-card",
  ".ch-card",
  ".enables-pill",
  ".wwb-item",
  ".footer-col",
  ".contact__info > div",
];

/* Media: fade in with a small scale settle. */
const FADE_SCALE = [
  ".welaunch-right__media",
  ".gs-left",
  ".ceo-avatar",
  ".work-details__image",
  ".details__image",
  ".quote-form__visual-img",
  ".order-hosting__visual-img",
  ".culture__right .img",
];

/* Opacity only, for elements that already carry a layout transform. */
const FADE = [".hosting__card", ".contact__map"];

const TARGETS = [
  { anim: "up", selector: FADE_UP.join(", ") },
  { anim: "scale", selector: FADE_SCALE.join(", ") },
  { anim: "fade", selector: FADE.join(", ") },
];

let viewportObserver = null;
let domObserver = null;
const settleTimers = new WeakMap();

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function settle(el) {
  const timer = settleTimers.get(el);
  if (timer) {
    clearTimeout(timer);
    settleTimers.delete(el);
  }
  el.removeEventListener("animationend", handleAnimationEnd);
  el.setAttribute(REVEAL_ATTR, "done");
  el.removeAttribute(ANIM_ATTR);
}

function handleAnimationEnd(event) {
  /* Ignore animations bubbling up from nested elements. */
  if (event.target !== event.currentTarget) return;
  if (!String(event.animationName).startsWith("v-")) return;
  settle(event.currentTarget);
}

function reveal(el) {
  el.setAttribute(REVEAL_ATTR, "in");
  el.addEventListener("animationend", handleAnimationEnd);
  settleTimers.set(
    el,
    setTimeout(() => {
      settleTimers.delete(el);
      settle(el);
    }, SETTLE_TIMEOUT)
  );
}

function handleIntersect(entries) {
  entries.forEach((entry) => {
    const el = entry.target;
    if (!entry.isIntersecting) {
      if (!el.isConnected && viewportObserver) viewportObserver.unobserve(el);
      return;
    }
    if (viewportObserver) viewportObserver.unobserve(el);
    reveal(el);
  });
}

function markTargets() {
  if (!viewportObserver) return;
  TARGETS.forEach(({ selector, anim }) => {
    document.querySelectorAll(selector).forEach((el) => {
      if (el.hasAttribute(REVEAL_ATTR)) return;
      el.setAttribute(REVEAL_ATTR, "pending");
      if (anim !== "up") el.setAttribute(ANIM_ATTR, anim);
      viewportObserver.observe(el);
    });
  });
}

function hasAddedElement(records) {
  for (let i = 0; i < records.length; i += 1) {
    const added = records[i].addedNodes;
    for (let j = 0; j < added.length; j += 1) {
      if (added[j].nodeType === 1) return true;
    }
  }
  return false;
}

export function stopScrollReveal() {
  if (viewportObserver) {
    viewportObserver.disconnect();
    viewportObserver = null;
  }
  if (domObserver) {
    domObserver.disconnect();
    domObserver = null;
  }
  /* Never leave content hidden if the engine is torn down mid-reveal. */
  document
    .querySelectorAll(`[${REVEAL_ATTR}="pending"]`)
    .forEach((el) => settle(el));
}

export function startScrollReveal() {
  if (
    typeof window === "undefined" ||
    typeof IntersectionObserver === "undefined" ||
    prefersReducedMotion()
  ) {
    return () => {};
  }

  stopScrollReveal();

  viewportObserver = new IntersectionObserver(handleIntersect, {
    root: null,
    rootMargin: "0px 0px -8% 0px",
    threshold: 0,
  });

  markTargets();

  /* CMS driven sections mount after their data arrives, so keep watching. */
  if (typeof MutationObserver !== "undefined") {
    domObserver = new MutationObserver((records) => {
      if (hasAddedElement(records)) markTargets();
    });
    domObserver.observe(document.body, { childList: true, subtree: true });
  }

  return stopScrollReveal;
}
